// ---------------------------------------------------------------------------
// The receipt of an offline sale, shaped from what the till itself charged.
//
// No server exists for this sale yet. Everything the paper needs is on the
// till: the lines at the prices the cached catalogue quoted, the rate they
// were priced at, the payments the cashier took, and a receipt number the
// server reserved for this till in advance. Laid over the `Sale` shape
// `ReceiptSheet` reads, exactly as a dispatched delivery's frozen figures are.
//
// The figures here are the ones the cart showed the cashier and the ones the
// queued payload will make the server record: the same helpers, the same
// rate, the same rounding. The server prices the sale again when it records
// it; a material difference is a conflict for a person, not something this
// file papers over.
// ---------------------------------------------------------------------------

import { cartSubtotal, cartSubtotalLbp, lineTotal, lineTotalLbp, unitPriceInLbp, type CartLine } from '../stores/cartStore';
import { toDecimal } from './format';
import type { Currency, PaymentDraft, Sale } from '../types';

export interface OfflineReceiptInput {
  offlineId: string;
  receiptNumber: string;
  lines: CartLine[];
  payments: PaymentDraft[];
  changeCurrency: Currency;
  /// The rate the till priced with, as a string.
  rate: string;
  deliveryFeeUsd: string;
  deliveryFeeLbp: string;
  note: string | null;
  fulfilment: 'PICKUP' | 'ON_TABLE' | 'DELIVERY' | null;
  tableLabel: string | null;
  cashier: { id: string; fullName: string; username: string };
  shift: { id: string; shiftNumber: string };
  createdAt: string;
}

export function offlineReceiptSale(input: OfflineReceiptInput): Sale {
  const rate = toDecimal(input.rate);
  const subtotalLbp = cartSubtotalLbp(input.lines, input.rate);
  const subtotalUsd = cartSubtotal(input.lines, input.rate);
  const feeLbp = toDecimal(input.deliveryFeeLbp || '0').toDecimalPlaces(0);
  const feeUsd = toDecimal(input.deliveryFeeUsd || '0').toDecimalPlaces(2);
  const totalLbp = subtotalLbp.plus(feeLbp);
  const totalUsd = subtotalUsd.plus(feeUsd).toDecimalPlaces(2);

  const payments = input.payments.map((payment, index) => {
    const amount = toDecimal(payment.amount);
    const amountUsd = payment.currency === 'USD' ? amount.toDecimalPlaces(2) : amount.dividedBy(rate).toDecimalPlaces(2);
    return {
      id: `${input.offlineId}:payment:${index}`,
      method: payment.method,
      currency: payment.currency,
      amount: payment.currency === 'USD' ? amount.toFixed(2) : amount.toFixed(0),
      amountUsd: amountUsd.toFixed(2),
      reference: payment.reference?.trim() || null,
    };
  });
  const paidUsd = payments.reduce((sum, payment) => sum.plus(toDecimal(payment.amountUsd)), toDecimal(0));
  const changeUsd = paidUsd.minus(totalUsd).greaterThan(0) ? paidUsd.minus(totalUsd).toDecimalPlaces(2) : toDecimal(0);
  const changeAmount = input.changeCurrency === 'USD' ? changeUsd : changeUsd.times(rate).toDecimalPlaces(0);

  return {
    id: input.offlineId,
    receiptNumber: input.receiptNumber,
    orderNumber: null,
    onlineOrder: null,
    receiptDesignVersionId: null,
    status: 'COMPLETED',
    subtotalUsd: subtotalUsd.toFixed(2),
    discountUsd: '0.00',
    discountReason: null,
    deliveryFeeUsd: feeUsd.toFixed(2),
    fulfilment: input.fulfilment,
    tableLabel: input.tableLabel,
    preparationDispatch: 'NORMAL',
    totalUsd: totalUsd.toFixed(2),
    voidedAt: null,
    voidReason: null,
    voidedBy: null,
    totalLbp: totalLbp.toFixed(0),
    subtotalLbp: subtotalLbp.toFixed(0),
    discountLbp: '0',
    deliveryFeeLbp: feeLbp.toFixed(0),
    exchangeRateUsdToLbp: input.rate,
    changeCurrency: input.changeCurrency,
    changeAmount: input.changeCurrency === 'USD' ? changeAmount.toFixed(2) : changeAmount.toFixed(0),
    changeUsd: changeUsd.toFixed(2),
    itemCount: input.lines.length,
    note: input.note,
    createdAt: input.createdAt,
    cashier: input.cashier,
    shift: input.shift,
    items: input.lines.map((line) => ({
      id: line.id,
      productId: line.productId,
      nameEn: line.nameEn,
      nameAr: line.nameAr,
      variantId: line.variantId,
      variantNameEn: line.variantNameEn,
      variantNameAr: line.variantNameAr,
      variantLabelEn: line.variantLabelEn,
      variantLabelAr: line.variantLabelAr,
      unitPriceLbp: unitPriceInLbp(line.unitPriceLbp, line.unitPrice, input.rate).toFixed(0),
      lineTotalLbp: lineTotalLbp(line, input.rate).toFixed(0),
      basePriceLbp: line.basePriceLbp ?? null,
      promotionType: line.promotionType ?? 'NONE',
      discountPercent: line.discountPercent ?? null,
      discountAmountLbp: line.discountAmountLbp ?? null,
      comboOfferId: line.comboOfferId ?? null,
      comboGroupKey: line.comboGroupKey ?? null,
      comboNameEn: line.comboNameEn ?? null,
      comboNameAr: line.comboNameAr ?? null,
      comboOfferPriceLbp: line.comboOfferPriceLbp ?? null,
      comboRegularPriceLbp: line.comboRegularPriceLbp ?? null,
      sku: line.sku,
      sellingUnit: line.sellingUnit,
      quantityMode: line.quantityMode,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      lineTotalUsd: lineTotal(line, input.rate).toFixed(2),
      note: line.note,
      refundedQuantity: '0',
    })),
    payments,
  } as Sale;
}
